import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import firebase from '../firebaseConfig';
import { shareLinkErrorMessage } from '../shareTarget';
import { routeMemberHome } from '../shareRouting';
import PasswordGate from '../components/PasswordGate';
import SharedPlayer from '../components/SharedPlayer';
import OpenInAppBanner from '../components/OpenInAppBanner';
import BrandBar from '../components/BrandBar';
import Login from '../components/Login';
import Helpers from '../Helpers';
import icon from '../icon.png';
import placeholder from '../gradient-placeholder.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faSpinner } from '@fortawesome/free-solid-svg-icons';

/**
 * /s/:token — tokenized one-off share link viewer. Could resolve to a
 * project, track, lyrics, or idea, depending on what the link's creator
 * picked. Calls `redeemShareLink` to get the payload + target type, then
 * routes to the appropriate sub-renderer.
 */
function SharedLink() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');
    const [errorMessage, setErrorMessage] = useState(null);
    const [pwError, setPwError] = useState(null);
    const [data, setData] = useState(null);
    const [password, setPassword] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [albumArtUrl, setAlbumArtUrl] = useState(placeholder);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const playerRef = useRef(null);

    const callRedeem = async (pwd) => {
        const fn = firebase.functions().httpsCallable('redeemShareLink');
        const res = await fn({ token, password: pwd || undefined });
        return res.data;
    };

    const routeIfMemberOrSignedIn = (result) => {
        if (result.targetType !== 'project' && result.targetType !== 'track') return false;
        if (routeMemberHome(result, navigate)) return true;
        if (result.targetType === 'project' && firebase.auth().currentUser) {
            navigate(
                `/home?sharedToken=${encodeURIComponent(token)}`,
                { replace: true, state: { redeemResult: result } },
            );
            return true;
        }
        return false;
    };

    const renderResult = (result) => {
        setData(result);
        setStatus('ready');
        if (result.targetType === 'track') {
            const t = result.payload?.track;
            if (t) {
                const meta = t.metadata || {};
                const currentRevId = meta['current revision'];
                const rev = currentRevId && t.revisions && t.revisions[currentRevId];
                setCurrentTrack({
                    id: result.trackId,
                    name: meta.name || 'Untitled',
                    duration: (rev && rev.metadata && rev.metadata.duration) || 0,
                });
            }
        }
    };

    // Wait for auth to settle so isArtistMember is correct.
    useEffect(() => {
        let cancelled = false;
        let fired = false;
        const unsub = firebase.auth().onAuthStateChanged(async () => {
            if (fired || cancelled) return;
            fired = true;
            try {
                const result = await callRedeem(null);
                if (cancelled) return;
                if (routeIfMemberOrSignedIn(result)) return;
                renderResult(result);
            } catch (e) {
                if (cancelled) return;
                handleErr(e);
            }
        });
        return () => { cancelled = true; unsub(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleErr = (e) => {
        const reason = e?.details?.reason;
        if (reason === 'password-required') {
            setStatus('pwGate');
            return;
        }
        if (reason === 'account-required') {
            setStatus('accountReq');
            return;
        }
        setStatus('error');
        setErrorMessage(shareLinkErrorMessage(e));
    };

    const onPasswordSubmit = async (pwd) => {
        try {
            const result = await callRedeem(pwd);
            setPassword(pwd);
            setPwError(null);
            if (routeIfMemberOrSignedIn(result)) return;
            renderResult(result);
        } catch (e) {
            const reason = e?.details?.reason;
            if (reason === 'password-required') {
                setPwError('Wrong password. Try again.');
                return;
            }
            handleErr(e);
        }
    };

    // Album art URL is resolved by the redeem CF and returned in
    // payload.albumArtUrl. Available for both project and track targets
    // (when the project has art set).
    useEffect(() => {
        if (!data) return;
        const url = data.payload && data.payload.albumArtUrl;
        setAlbumArtUrl(url || placeholder);
    }, [data]);

    // Build tracks list (project target).
    const tracks = useMemo(() => {
        if (!data || data.targetType !== 'project') return [];
        const project = data.payload?.project;
        if (!project?.tracks) return [];
        const list = Object.entries(project.tracks).map(([id, track]) => {
            const meta = (track && track.metadata) || {};
            const currentRevId = meta['current revision'];
            const rev = currentRevId && track.revisions && track.revisions[currentRevId];
            const revMeta = (rev && rev.metadata) || {};
            return {
                id,
                name: meta.name || 'Untitled',
                duration: revMeta.duration || 0,
                orderWeight: meta['order weight'] || 0,
                isLocked: meta.locked === true,
            };
        });
        list.sort((a, b) => a.orderWeight - b.orderWeight);
        return list;
    }, [data]);

    const audioFetcher = useMemo(() => {
        return async (trackId) => {
            const fn = firebase.functions()
                .httpsCallable('getSharedTrackAudioURL');
            const res = await fn({ token, trackId, password: password || undefined });
            return res.data;
        };
    }, [token, password]);

    const playLogger = useMemo(() => {
        return async (trackId, ms, sessionId) => {
            const fn = firebase.functions().httpsCallable('logSharedTrackPlay');
            await fn({ token, trackId, ms, sessionId });
        };
    }, [token]);

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------

    if (status === 'loading') {
        return (
            <div className="splash-screen">
                <img src={icon} alt="Demodeck" className="splash-logo" />
            </div>
        );
    }
    if (status === 'error') {
        return (
            <div className="shared-error">
                <img src={icon} alt="Demodeck" className="shared-error-logo" />
                <div className="shared-error-message">{errorMessage}</div>
            </div>
        );
    }
    if (status === 'pwGate') {
        return <PasswordGate onSubmit={onPasswordSubmit} errorMessage={pwError} />;
    }
    if (status === 'accountReq') {
        const onSignedIn = () => {
            setStatus('loading');
            (async () => {
                try {
                    const result = await callRedeem(password);
                    if (routeIfMemberOrSignedIn(result)) return;
                    renderResult(result);
                } catch (e) { handleErr(e); }
            })();
        };
        return (
            <div className="shared-account-gate">
                <img src={icon} alt="Demodeck" className="shared-account-gate-logo" />
                <div className="shared-account-gate-title">Sign in to open</div>
                <div className="shared-account-gate-subtitle">
                    The owner restricted this link to Demodeck users.
                </div>
                <Login onLoginSuccess={onSignedIn} embedded />
            </div>
        );
    }

    // Ready — branch on targetType.
    if (data.targetType === 'project') {
        const project = data.payload?.project;
        const projectName = (project?.metadata?.name) || 'Untitled';
        const totalSec = tracks.reduce((acc, t) => acc + (t.duration || 0), 0);
        const firstPlayable = tracks.find((t) => !t.isLocked) || null;
        return (
            <>
                <BrandBar />
                <div className="shared-page">
                    <OpenInAppBanner />
                <div className="shared-page-header">
                    <img src={albumArtUrl} alt="album art" className="shared-page-art" />
                    <div className="shared-page-title-row">
                        <div className="shared-page-meta">
                            <div className="shared-page-title">{projectName}</div>
                            {data.artistName && (
                                <div className="shared-page-artist">{data.artistName}</div>
                            )}
                            <div className="shared-page-subtitle">
                                {tracks.length} track{tracks.length === 1 ? '' : 's'} · {Helpers.formatDuration(totalSec)}
                            </div>
                        </div>
                        <button
                            className="shared-page-play"
                            onClick={() => {
                                if (currentTrack) playerRef.current?.togglePlay();
                                else if (firstPlayable) setCurrentTrack(firstPlayable);
                            }}
                            disabled={!firstPlayable || isLoading}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            <FontAwesomeIcon
                                icon={isLoading ? faSpinner : (isPlaying ? faPause : faPlay)}
                                className={isLoading ? 'track-spin' : ''}
                            />
                        </button>
                    </div>
                </div>
                <ol className="shared-page-tracklist">
                    {tracks.map((track, i) => (
                        <li
                            key={track.id}
                            className={`shared-page-track${currentTrack?.id === track.id ? ' is-playing' : ''}${track.isLocked ? ' is-locked' : ''}`}
                            onClick={() => !track.isLocked && setCurrentTrack(track)}
                        >
                            <span className="shared-page-track-number">{i + 1}</span>
                            <span className="shared-page-track-name">{track.name}</span>
                            <span className="shared-page-track-duration">
                                {Helpers.formatDuration(track.duration || 0)}
                            </span>
                        </li>
                    ))}
                </ol>
                <SharedPlayer
                    ref={playerRef}
                    tracks={tracks}
                    currentTrack={currentTrack}
                    setCurrentTrack={setCurrentTrack}
                    audioFetcher={audioFetcher}
                    playLogger={playLogger}
                    albumArtUrl={albumArtUrl}
                    projectId={data.projectId}
                    projectName={projectName}
                    artistName={data.artistName}
                    onPlayingChange={setIsPlaying}
                    onLoadingChange={setIsLoading}
                />
                </div>
            </>
        );
    }

    if (data.targetType === 'track') {
        const t = data.payload?.track;
        const projectName = data.payload?.projectMetadata?.name || '';
        const trackName = (t?.metadata?.name) || 'Untitled';
        const artistName = data.artistName || null;
        const trackDuration = currentTrack?.duration || 0;
        return (
            <>
                <BrandBar />
                <div className="shared-page shared-page--track">
                    <OpenInAppBanner />
                <div className="shared-page-header">
                    <img src={albumArtUrl} alt="album art" className="shared-page-art" />
                    <div className="shared-page-title-row">
                        <div className="shared-page-meta">
                            <div className="shared-page-title">{trackName}</div>
                            {artistName && (
                                <div className="shared-page-artist">{artistName}</div>
                            )}
                            <div className="shared-page-subtitle">
                                {projectName ? `${projectName} · ` : ''}
                                {Helpers.formatDuration(trackDuration)}
                            </div>
                        </div>
                        <button
                            className="shared-page-play"
                            onClick={() => {
                                if (currentTrack) playerRef.current?.togglePlay();
                            }}
                            disabled={!currentTrack || isLoading}
                            aria-label={isPlaying ? 'Pause track' : 'Play track'}
                        >
                            <FontAwesomeIcon
                                icon={isLoading ? faSpinner : (isPlaying ? faPause : faPlay)}
                                className={isLoading ? 'track-spin' : ''}
                            />
                        </button>
                    </div>
                </div>
                <SharedPlayer
                    ref={playerRef}
                    tracks={currentTrack ? [currentTrack] : []}
                    currentTrack={currentTrack}
                    setCurrentTrack={setCurrentTrack}
                    audioFetcher={audioFetcher}
                    playLogger={playLogger}
                    albumArtUrl={albumArtUrl}
                    projectId={data.projectId}
                    projectName={projectName}
                    artistName={artistName}
                    onPlayingChange={setIsPlaying}
                    onLoadingChange={setIsLoading}
                />
                </div>
            </>
        );
    }

    if (data.targetType === 'lyrics') {
        const ly = data.payload?.lyrics;
        const name = (ly?.metadata?.name) || 'Lyrics';
        const body = (ly?.metadata?.lyrics) || '';
        return (
            <>
                <BrandBar />
                <div className="shared-page">
                    <OpenInAppBanner />
                <div className="shared-page-header">
                    <div className="shared-page-meta">
                        <div className="shared-page-title">{name}</div>
                        <div className="shared-page-subtitle">Lyrics</div>
                    </div>
                </div>
                <pre className="shared-page-lyrics">{body}</pre>
                </div>
            </>
        );
    }

    if (data.targetType === 'idea') {
        const idea = data.payload?.idea;
        const name = (idea?.metadata?.name) || 'Idea';
        return (
            <>
                <BrandBar />
                <div className="shared-page">
                    <OpenInAppBanner />
                <div className="shared-page-header">
                    <div className="shared-page-meta">
                        <div className="shared-page-title">{name}</div>
                        <div className="shared-page-subtitle">Idea</div>
                    </div>
                </div>
                {/* Idea audio playback uses the same audioFetcher path; for
                    the moment we point the player at this single idea. */}
                <SharedPlayer
                    tracks={[{ id: data.ideaId, name, duration: 0 }]}
                    currentTrack={{ id: data.ideaId, name, duration: 0 }}
                    setCurrentTrack={() => {}}
                    audioFetcher={audioFetcher}
                    playLogger={playLogger}
                />
                </div>
            </>
        );
    }

    return (
        <div className="shared-error">
            <div className="shared-error-message">Unknown link type.</div>
        </div>
    );
}

export default SharedLink;
